/**
 * Comment se lit la situation financière d'une famille.
 *
 * ## Pourquoi ces règles ne sont pas dans l'écran
 *
 * Un même chiffre doit se lire pareil partout : sur cette tuile, dans un
 * rappel, dans un export. Les mettre ici les rend testables, et empêche qu'un
 * second écran dise autre chose que le premier.
 *
 * ## L'avertissement qui compte plus que les chiffres
 *
 * Mesuré en production le 4 septembre 2026 : la vue annonce **329 207,05 €**
 * de reste dû, mais seules **48 factures sur 3 689** portent un paiement
 * rapproché dans le nouveau modèle. L'ancien circuit, lui, en connaît 447 de
 * payées, pour 54 740,00 €.
 *
 * Le reste dû est donc *techniquement juste* — il se déduit des allocations,
 * et d'elles seules — et *pratiquement trompeur* tant que l'historique des
 * paiements n'est pas repris. Un écran qui l'afficherait sans le dire ferait
 * croire à 329 000 € d'arriérés.
 *
 * `ecartAncienCircuit()` est la fonction qui oblige à le dire.
 */

/** Les six états qu'une famille peut avoir, tels que la base les nomme. */
export const SITUATIONS = [
  'en_retard',
  'partiel',
  'impaye',
  'provision',
  'solde',
  'aucune_facture',
] as const;
export type Situation = (typeof SITUATIONS)[number];

export interface SituationLabel {
  /** Ce qui s'affiche dans la pastille. */
  label: string;
  /** Ce que cela veut dire, en une phrase. */
  explication: string;
  /** Le ton visuel : `alerte` réclame une action, `neutre` non. */
  ton: 'alerte' | 'attention' | 'positif' | 'neutre';
}

export const LIBELLES: Record<Situation, SituationLabel> = {
  en_retard: {
    label: 'En retard',
    explication: 'Une échéance est dépassée et la facture réclame encore.',
    ton: 'alerte',
  },
  partiel: {
    label: 'Partiel',
    explication: 'Un paiement est arrivé, mais il ne couvre pas tout.',
    ton: 'attention',
  },
  impaye: {
    label: 'À payer',
    explication: "Rien n'est encore reçu, et l'échéance n'est pas dépassée.",
    ton: 'attention',
  },
  provision: {
    label: 'Provision',
    explication: 'Plus rien n\'est dû, et de l\'argent reste au crédit de la famille.',
    ton: 'positif',
  },
  solde: {
    label: 'Soldé',
    explication: 'Tout ce qui était dû a été reçu.',
    ton: 'positif',
  },
  aucune_facture: {
    label: 'Aucune facture',
    explication: 'Ce compte existe mais n\'a jamais été facturé.',
    ton: 'neutre',
  },
};

export function libelle(situation: string): SituationLabel {
  return (
    LIBELLES[situation as Situation] ?? {
      label: situation || 'Inconnu',
      explication: 'État non reconnu.',
      ton: 'neutre' as const,
    }
  );
}

/** Une famille, telle que la vue la rend. */
export interface FamilySituation {
  userId: string;
  prenom: string | null;
  nom: string | null;
  email: string | null;
  accountType: string | null;
  organisationName: string | null;
  invoiceCount: number;
  invoicedCents: number;
  creditNotesCents: number;
  netCents: number;
  receivedCents: number;
  outstandingCents: number;
  cancelledOutstandingCents: number;
  overpaidCents: number;
  unpaidCount: number;
  provisionCents: number;
  registrationsCount: number;
  lastPaymentDate: string | null;
  oldestDueDate: string | null;
  daysOverdue: number;
  legacyPaidCents: number;
  legacyPaidInvoiceCount: number;
  legacyAhead: boolean;
  situation: string;
}

/** Le nom sous lequel une famille s'affiche. */
export function nomFamille(f: FamilySituation): string {
  if (f.accountType === 'organisation' && f.organisationName) return f.organisationName;
  const nom = [f.prenom, f.nom].filter((x) => x && x.trim() !== '').join(' ');
  return nom !== '' ? nom : (f.email ?? 'Compte sans nom');
}

/**
 * L'ancien circuit en sait-il plus que le nouveau, et de combien ?
 *
 * Rend `null` quand les deux concordent — il n'y a alors rien à signaler, et
 * signaler quand il n'y a rien à dire est la meilleure façon de faire ignorer
 * un avertissement.
 */
export function ecartAncienCircuit(f: FamilySituation): { ecartCents: number; factures: number } | null {
  if (!f.legacyAhead) return null;
  const ecart = f.legacyPaidCents - f.receivedCents;
  if (ecart <= 0) return null;
  return { ecartCents: ecart, factures: f.legacyPaidInvoiceCount };
}

/**
 * Le reste dû de cette famille est-il une dette sur laquelle on peut agir ?
 *
 * Non quand l'ancien circuit connaît un paiement que le nouveau ignore :
 * envoyer un rappel dans ce cas réclamerait de l'argent déjà reçu. C'est
 * précisément la faute que cette fonction existe pour éviter.
 */
export function detteExploitable(f: FamilySituation): boolean {
  return f.outstandingCents > 0 && !f.legacyAhead;
}

/** Les tranches d'ancienneté, dans l'ordre où elles se lisent. */
export const TRANCHES = [
  { cle: 'due_soon_cents', label: 'Non échu' },
  { cle: 'overdue_30_cents', label: '1 à 30 jours' },
  { cle: 'overdue_90_cents', label: '31 à 90 jours' },
  { cle: 'overdue_365_cents', label: '91 jours à 1 an' },
  { cle: 'overdue_beyond_cents', label: 'Plus d\'un an' },
] as const;

const EUR = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });

/** Un montant en centimes, écrit en euros. */
export function euros(cents: number | null | undefined): string {
  return EUR.format((cents ?? 0) / 100);
}

/** Un retard en jours, dit en mots. */
export function retard(jours: number): string {
  if (jours <= 0) return '';
  if (jours === 1) return '1 jour';
  if (jours < 31) return `${jours} jours`;
  if (jours < 365) return `${Math.round(jours / 30)} mois`;
  const annees = Math.floor(jours / 365);
  const mois = Math.round((jours % 365) / 30);
  if (mois === 0) return annees === 1 ? '1 an' : `${annees} ans`;
  return `${annees} an${annees > 1 ? 's' : ''} et ${mois} mois`;
}
